/**
 * Keyboard shortcut system.
 *
 * Three layers:
 *   1. ACTIONS: declarative list of every command that can have a
 *      shortcut, plus a default chord (or null if unbound by default).
 *   2. bindings: user-overridable map actionId → chord (or null), kept
 *      in localStorage. Functions to import/export the bindings as
 *      JSON for sharing across machines / co-workers.
 *   3. handlers + dispatcher: components register `useShortcutHandler`
 *      with a function for an action; the global dispatcher (running
 *      from the app shell) listens to keydown, matches the chord
 *      against the current bindings, and fires the handler.
 *
 * The chord encoding is stable across platforms:
 *   - Modifiers, in fixed order: "Ctrl", "Alt", "Shift"
 *   - Then the key, uppercase for letters, KeyboardEvent.key for the rest
 *   - Joined with "+", e.g. "H", "Shift+H", "Ctrl+F1", "Escape", "?"
 *
 * Two chords pointing at the same action are NOT supported. A chord
 * may only bind one action at a time; repurposing a chord automatically
 * frees it from any previous action it was on.
 */

import { useEffect, useRef } from 'react'

export interface ShortcutAction {
  id: string
  group: string
  label: string
  defaultChord: string | null
}

export const ACTIONS: ShortcutAction[] = [
  // Câmara
  {
    id: 'viewer.focusSelection',
    group: 'Câmara',
    label: 'Focar seleção',
    defaultChord: 'F',
  },
  {
    id: 'viewer.setPivotHere',
    group: 'Câmara',
    label: 'Definir eixo de rotação na seleção',
    defaultChord: 'P',
  },
  // Seleção
  {
    id: 'viewer.tabCycleNext',
    group: 'Seleção',
    label: 'Ciclar elementos sobrepostos (próximo)',
    defaultChord: 'Tab',
  },
  {
    id: 'viewer.tabCyclePrev',
    group: 'Seleção',
    label: 'Ciclar elementos sobrepostos (anterior)',
    defaultChord: 'Shift+Tab',
  },
  {
    id: 'viewer.clearOrCancel',
    group: 'Seleção',
    label: 'Limpar seleção / cancelar ação',
    defaultChord: 'Escape',
  },
  // Visibilidade
  {
    id: 'viewer.hideSelected',
    group: 'Visibilidade',
    label: 'Esconder seleção',
    defaultChord: 'H',
  },
  {
    id: 'viewer.isolateSelected',
    group: 'Visibilidade',
    label: 'Isolar seleção',
    defaultChord: 'I',
  },
  {
    id: 'viewer.invertSelection',
    group: 'Seleção',
    label: 'Inverter seleção',
    defaultChord: 'Shift+I',
  },
  {
    id: 'viewer.showAll',
    group: 'Visibilidade',
    label: 'Mostrar tudo',
    defaultChord: 'Alt+H',
  },
  {
    id: 'viewer.toggleGhost',
    group: 'Visibilidade',
    label: 'Ghost mode (toggle)',
    defaultChord: 'G',
  },
  // Cena
  {
    id: 'viewer.toggleGrid',
    group: 'Cena',
    label: 'Mostrar/esconder grelha',
    defaultChord: 'R',
  },
  {
    id: 'viewer.toggleEdges',
    group: 'Cena',
    label: 'Mostrar/esconder arestas',
    defaultChord: 'E',
  },
  {
    id: 'viewer.toggleClassColors',
    group: 'Cena',
    label: 'Cores por classe (toggle)',
    defaultChord: null,
  },
  {
    id: 'viewer.toggleBgMode',
    group: 'Cena',
    label: 'Fundo claro/escuro',
    defaultChord: null,
  },
  {
    id: 'viewer.toggleHideSpaces',
    group: 'Cena',
    label: 'Esconder/mostrar IfcSpace/Zone',
    defaultChord: 'Q',
  },
  {
    id: 'viewer.toggleSection',
    group: 'Cena',
    label: 'Section box (toggle)',
    defaultChord: 'S',
  },
  {
    id: 'viewer.toggleSectionBoxCaixa',
    group: 'Cena',
    label: 'Mostrar/esconder a caixa de corte (o corte mantém-se)',
    defaultChord: 'Shift+X',
  },
  {
    id: 'viewer.toggleAppearancePanel',
    group: 'Cena',
    label: 'Janela Aparência (toggle)',
    defaultChord: 'A',
  },
  {
    id: 'viewer.toggleColorByPanel',
    group: 'Cena',
    label: 'Painel Colorir por… (toggle)',
    defaultChord: 'C',
  },
  {
    id: 'viewer.toggleFilterPanel',
    group: 'Seleção',
    label: 'Painel Filtro multi-critério (toggle)',
    defaultChord: 'Shift+F',
  },
  // Selecção avançada (Revit-style)
  {
    id: 'viewer.selectAllOfClass',
    group: 'Seleção',
    label: 'Selecionar todas as instâncias da classe',
    defaultChord: 'Shift+C',
  },
  {
    id: 'viewer.selectAllOfSameType',
    group: 'Seleção',
    label: 'Selecionar todas as instâncias do tipo',
    defaultChord: 'Shift+T',
  },
  {
    id: 'viewer.selectAllInStorey',
    group: 'Seleção',
    label: 'Selecionar tudo no piso (storey)',
    defaultChord: 'Shift+S',
  },
  {
    id: 'viewer.selectAllInBuilding',
    group: 'Seleção',
    label: 'Selecionar tudo no edifício',
    defaultChord: 'Shift+B',
  },
  // Ribbon
  {
    id: 'ribbon.toggleCollapse',
    group: 'Ribbon',
    label: 'Colapsar/expandir ribbon',
    defaultChord: 'Ctrl+F1',
  },
  // Ficheiro
  {
    id: 'file.openIfc',
    group: 'Ficheiro',
    label: 'Abrir modelo (substituir)',
    defaultChord: 'Ctrl+Shift+L',
  },
  {
    id: 'file.addIfc',
    group: 'Ficheiro',
    label: 'Adicionar IFC (federar)',
    defaultChord: null,
  },
  // Sessão
  {
    id: 'session.save',
    group: 'Sessão',
    label: 'Guardar sessão (.vn2r-session)',
    defaultChord: 'Ctrl+Shift+S',
  },
  {
    id: 'session.open',
    group: 'Sessão',
    label: 'Abrir sessão (.vn2r-session)',
    defaultChord: 'Ctrl+Shift+O',
  },
  {
    id: 'session.close',
    group: 'Sessão',
    label: 'Fechar sessão',
    // Chrome reserves Ctrl+Shift+W and Ctrl+W at the OS level; no
    // single-key default for this destructive action; user binds
    // manually if they want one.
    defaultChord: null,
  },
  // Topbar
  {
    id: 'topbar.toggleDiagnostics',
    group: 'Topbar',
    label: 'Diagnóstico (popover)',
    defaultChord: 'Shift+D',
  },
  {
    id: 'topbar.toggleModelInfo',
    group: 'Topbar',
    label: 'Info do modelo (janela)',
    defaultChord: 'Shift+M',
  },
  {
    id: 'topbar.toggleHistory',
    group: 'Topbar',
    label: 'Histórico (popover)',
    defaultChord: null,
  },
  // Ajuda
  {
    id: 'ribbon.openHelp',
    group: 'Ajuda',
    label: 'Abrir/fechar painel de atalhos',
    defaultChord: '?',
  },
]

// ---------------------------------------------------------------------------
// Bindings: localStorage persistence + import/export
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'vn2r.shortcuts.bindings.v1'
type Bindings = Record<string, string | null>
let bindings: Bindings = loadBindings()
const subs = new Set<() => void>()

/** Forma canónica de um atalho gravado. Os atalhos guardados antes de o
 *  Shift deixar de entrar nos símbolos ("Shift+?") passam a "?", que é
 *  o que `eventToChord` gera hoje. */
export function normalizarChord(chord: string | null): string | null {
  if (chord === null) return null
  const m = /^((?:Ctrl\+)?(?:Alt\+)?)Shift\+(.)$/.exec(chord)
  if (m && !/[a-z ]/i.test(m[2])) return m[1] + m[2]
  return chord
}

function normalizarBindings(b: Bindings): Bindings {
  const out: Bindings = {}
  for (const [id, chord] of Object.entries(b)) {
    out[id] =
      chord === null || typeof chord === 'string' ? normalizarChord(chord) : null
  }
  return out
}

function loadBindings(): Bindings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object') {
      return normalizarBindings(parsed as Bindings)
    }
  } catch {
    /* ignore */
  }
  return {}
}

function saveBindings() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings))
  } catch {
    /* ignore */
  }
  for (const fn of subs) fn()
}

export function getChord(actionId: string): string | null {
  if (actionId in bindings) return bindings[actionId]
  return ACTIONS.find((a) => a.id === actionId)?.defaultChord ?? null
}

/** Set a chord for the action. If the chord was previously bound to
 *  another action, that action is automatically unbound (we never let
 *  two actions share the same chord). Pass null to remove the binding
 *  without falling back to the default. */
export function setChord(actionId: string, chord: string | null): void {
  if (chord !== null) {
    for (const action of ACTIONS) {
      if (action.id === actionId) continue
      if (getChord(action.id) === chord) {
        bindings[action.id] = null
      }
    }
  }
  bindings[actionId] = chord
  saveBindings()
}

/** Restore the action to its declared default chord. */
export function resetChord(actionId: string): void {
  delete bindings[actionId]
  saveBindings()
}

/** Wipe all user customizations and revert to declared defaults. */
export function resetAllBindings(): void {
  bindings = {}
  saveBindings()
}

export function subscribeBindings(fn: () => void): () => void {
  subs.add(fn)
  return () => {
    subs.delete(fn)
  }
}

/** Serialize current overrides + an explicit snapshot of every action's
 *  effective chord. The "effective" snapshot is what you want to send
 *  to a co-worker; the overrides alone could leave their machine on a
 *  different default if they have an older app version. */
export function exportBindings(): string {
  const effective: Bindings = {}
  for (const action of ACTIONS) {
    effective[action.id] = getChord(action.id)
  }
  const payload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    application: 'VN2R openBIM Hub',
    bindings: effective,
  }
  return JSON.stringify(payload, null, 2)
}

export function importBindings(json: string): { applied: number; skipped: string[] } {
  const parsed = JSON.parse(json) as { bindings?: Bindings }
  if (!parsed?.bindings || typeof parsed.bindings !== 'object') {
    throw new Error('Ficheiro de atalhos inválido (sem campo bindings).')
  }
  const validIds = new Set(ACTIONS.map((a) => a.id))
  const next: Bindings = {}
  const skipped: string[] = []
  let applied = 0
  for (const [id, chord] of Object.entries(parsed.bindings)) {
    if (!validIds.has(id)) {
      skipped.push(id)
      continue
    }
    if (chord === null || typeof chord === 'string') {
      next[id] = normalizarChord(chord)
      applied += 1
    }
  }
  bindings = next
  saveBindings()
  return { applied, skipped }
}

// ---------------------------------------------------------------------------
// Dispatcher: chord parser + handler registry + global keydown listener
// ---------------------------------------------------------------------------

/** Convert a KeyboardEvent into the chord encoding used by ACTIONS. */
export function eventToChord(e: KeyboardEvent): string {
  const parts: string[] = []
  let key = e.key
  // Um símbolo já traz o Shift dentro: "?" escreve-se com Shift+/ no
  // teclado inglês e com Shift+' no português, e o que interessa é o
  // carácter. Sem isto, "?" chegava como "Shift+?" e nunca casava.
  const simbolo = key.length === 1 && !/[a-z]/i.test(key) && key !== ' '
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl')
  if (e.altKey) parts.push('Alt')
  if (e.shiftKey && !simbolo) parts.push('Shift')
  // Ignore pure modifier keydowns: they are part of the chord, not a
  // chord on their own.
  if (
    key === 'Control' ||
    key === 'Meta' ||
    key === 'Alt' ||
    key === 'Shift'
  ) {
    return ''
  }
  if (key.length === 1 && /[a-z]/i.test(key)) {
    key = key.toUpperCase()
  }
  parts.push(key)
  return parts.join('+')
}

/** A handler may return `false` to say "not mine this time": the key
 *  then keeps its normal browser behaviour (Tab moves the focus when
 *  there is nothing to cycle). */
export type ShortcutHandler = () => unknown

const handlers = new Map<string, ShortcutHandler>()

// Roles ARIA de controlos onde as teclas são texto ou escolha, não
// atalhos: num <select>, escrever "h" escolhe a opção começada por h.
const ROLES_DE_ESCRITA = new Set([
  'textbox',
  'searchbox',
  'combobox',
  'listbox',
  'spinbutton',
])

export function isTypingTarget(t: EventTarget | null): boolean {
  if (!t || !(t instanceof HTMLElement)) return false
  const tag = t.tagName
  if (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    t.isContentEditable
  ) {
    return true
  }
  const role = t.getAttribute('role')
  return role !== null && ROLES_DE_ESCRITA.has(role)
}

// Tab e Shift+Tab só são atalho quando o foco não está num controlo:
// num botão, numa ligação ou num menu, Tab é o browser a passar ao
// controlo seguinte, que é como se navega a interface pelo teclado.
const TECLAS_DE_FOCO = new Set(['Tab', 'Shift+Tab'])

function alvoNeutro(t: EventTarget | null): boolean {
  if (!t || !(t instanceof HTMLElement)) return true
  return (
    t === document.body ||
    t === document.documentElement ||
    t.tagName === 'CANVAS'
  )
}

// Quem está aberto por cima de tudo (visita guiada, menu, gaveta) e se
// fecha com Escape. O último a abrir é o primeiro a fechar, e enquanto
// houver algum o Escape não chega à cascata do viewer (limpar a
// selecção, sair de modos).
const consumidoresEscape: Array<{ fn: () => void }> = []

/** Regista quem fecha com Escape. Devolve a função que o retira. */
export function registarEscape(fn: () => void): () => void {
  const entrada = { fn }
  consumidoresEscape.push(entrada)
  return () => {
    const i = consumidoresEscape.indexOf(entrada)
    if (i >= 0) consumidoresEscape.splice(i, 1)
  }
}

/** Hook: enquanto `ativo`, o Escape chama `fn` antes de mais nada. */
export function useEscape(ativo: boolean, fn: () => void): void {
  const ref = useRef(fn)
  useEffect(() => {
    ref.current = fn
  })
  useEffect(() => {
    if (!ativo) return
    return registarEscape(() => ref.current())
  }, [ativo])
}

/** Register a handler for an action. Returns an unregister fn so the
 *  caller can clean up (intended to be wrapped in useEffect). */
export function registerHandler(
  actionId: string,
  fn: ShortcutHandler,
): () => void {
  handlers.set(actionId, fn)
  return () => {
    if (handlers.get(actionId) === fn) handlers.delete(actionId)
  }
}

/** React hook wrapper around registerHandler. */
export function useShortcutHandler(
  actionId: string,
  fn: ShortcutHandler,
  deps: ReadonlyArray<unknown> = [],
): void {
  useEffect(() => {
    return registerHandler(actionId, fn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, ...deps])
}

/** Mount this once at the top of the app: attaches a window keydown
 *  listener that maps chords to registered handlers. */
export function useShortcutDispatcher(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      // Skip while a chord-listening UI is grabbing keys (rebind mode).
      if ((window as unknown as { __vn2rChordCapture?: boolean })
          .__vn2rChordCapture) {
        return
      }
      const chord = eventToChord(e)
      if (!chord) return
      if (chord === 'Escape' && consumidoresEscape.length > 0) {
        e.preventDefault()
        e.stopPropagation()
        consumidoresEscape[consumidoresEscape.length - 1].fn()
        return
      }
      if (TECLAS_DE_FOCO.has(chord) && !alvoNeutro(e.target)) return
      for (const action of ACTIONS) {
        if (getChord(action.id) === chord) {
          const handler = handlers.get(action.id)
          if (handler) {
            if (handler() === false) return
            e.preventDefault()
            e.stopPropagation()
          }
          return
        }
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () =>
      window.removeEventListener('keydown', onKey, { capture: true })
  }, [])
}

/** Helper for the rebind UI: capture exactly one chord and call back
 *  with it. Sets a window flag to silence the dispatcher while the
 *  user is binding. Returns a cancel function. */
export function captureNextChord(
  cb: (chord: string | 'cancel') => void,
): () => void {
  const w = window as unknown as { __vn2rChordCapture?: boolean }
  w.__vn2rChordCapture = true
  const onKey = (e: KeyboardEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.key === 'Escape') {
      cleanup()
      cb('cancel')
      return
    }
    const chord = eventToChord(e)
    if (!chord) return
    cleanup()
    cb(chord)
  }
  function cleanup() {
    window.removeEventListener('keydown', onKey, { capture: true })
    w.__vn2rChordCapture = false
  }
  window.addEventListener('keydown', onKey, { capture: true })
  return cleanup
}
